import { useEffect, useState, useCallback } from "react";
import { TrendingUp, TrendingDown, FlaskConical } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import { api, inr } from "@/lib/api";

export const NAVY = "#0B132B";
export const TEAL = "#14B8A6";
export const CHART_COLORS = ["#14B8A6", "#0B132B", "#6366F1", "#F59E0B", "#EF4444", "#10B981", "#8B5CF6", "#0EA5E9"];

export const inrShort = (n) => {
  n = Number(n || 0);
  if (Math.abs(n) >= 1e7) return "₹" + (n / 1e7).toFixed(2) + " Cr";
  if (Math.abs(n) >= 1e5) return "₹" + (n / 1e5).toFixed(2) + " L";
  return inr(n);
};

const DEMO_KEY = "zrope_include_demo";
export const isPreviewEnv = () => typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com");
export const getIncludeDemo = () => {
  const v = localStorage.getItem(DEMO_KEY);
  if (v === null) return isPreviewEnv();
  return v === "true";
};
export const setIncludeDemo = (v) => localStorage.setItem(DEMO_KEY, String(v));

export const RANGES = [
  { key: "1", label: "Today", days: 1 },
  { key: "7", label: "7 Days", days: 7 },
  { key: "30", label: "30 Days", days: 30 },
  { key: "90", label: "Quarter", days: 90 },
  { key: "365", label: "Year", days: 365 },
];

// Central analytics fetch hook — one filtering mechanism for all admin analytics/report views.
export function useAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [demo, setDemo] = useState(getIncludeDemo());
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [filters, setFilters] = useState({ lender: "ALL", payment_type: "ALL", confirmation_status: "ALL" });
  const load = useCallback(() => {
    const p = new URLSearchParams();
    p.set("include_demo", String(demo));
    if (custom.from && custom.to) { p.set("from_date", custom.from); p.set("to_date", custom.to); }
    else p.set("days", String(days));
    if (filters.lender && filters.lender !== "ALL") p.set("lender", filters.lender);
    if (filters.payment_type && filters.payment_type !== "ALL") p.set("payment_type", filters.payment_type);
    if (filters.confirmation_status && filters.confirmation_status !== "ALL") p.set("confirmation_status", filters.confirmation_status);
    api.get(`/admin/analytics?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days, demo, custom, filters]);
  useEffect(() => { load(); }, [load]);
  const toggleDemo = (v) => { setIncludeDemo(v); setDemo(v); };
  return { data, days, setDays, demo, toggleDemo, custom, setCustom, filters, setFilters, reload: load };
}

export function DemoToggle({ demo, onChange }) {
  return (
    <button onClick={() => onChange(!demo)} data-testid="demo-toggle"
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${demo ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-500"}`}>
      <FlaskConical size={13} /> Test data {demo ? "ON" : "OFF"}
    </button>
  );
}

export function Toolbar({ a }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
        {RANGES.map((r) => (
          <button key={r.key} data-testid={`range-${r.key}`}
            onClick={() => { a.setCustom({ from: "", to: "" }); a.setDays(r.days); }}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${!a.custom.from && a.days === r.days ? "bg-[#0B132B] text-white" : "text-slate-500 hover:text-[#0B132B]"}`}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1 text-xs">
        <input type="date" value={a.custom.from} data-testid="range-from" onChange={(e) => a.setCustom({ ...a.custom, from: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 outline-none focus:border-[#14B8A6]" />
        <span className="text-slate-400">–</span>
        <input type="date" value={a.custom.to} data-testid="range-to" onChange={(e) => a.setCustom({ ...a.custom, to: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 outline-none focus:border-[#14B8A6]" />
      </div>
      <DemoToggle demo={a.demo} onChange={a.toggleDemo} />
    </div>
  );
}

export function Kpi({ label, value, delta, icon: Icon, hint, accent = TEAL }) {
  const up = delta > 0, down = delta < 0;
  return (
    <div className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        {Icon && <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ backgroundColor: accent + "1a", color: accent }}><Icon size={16} /></span>}
      </div>
      <p className="mt-2 font-display text-2xl font-bold text-[#0B132B]">{value}</p>
      <div className="mt-1 flex items-center gap-2">
        {delta !== null && delta !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-emerald-600" : down ? "text-red-500" : "text-slate-400"}`}>
            {up ? <TrendingUp size={12} /> : down ? <TrendingDown size={12} /> : null}{Math.abs(delta)}%
          </span>
        )}
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
    </div>
  );
}

export function Panel({ title, subtitle, right, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      <div className="flex items-center justify-between">
        <div><h3 className="font-display font-bold text-[#0B132B]">{title}</h3>{subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}</div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function TrendArea({ data, dataKey = "collection", money = true, height = 260 }) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
        <AreaChart data={data} margin={{ left: 4, right: 8 }}>
          <defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={TEAL} stopOpacity={0.32} /><stop offset="100%" stopColor={TEAL} stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => money ? inrShort(v).replace("₹", "") : v} width={44} />
          <Tooltip formatter={(v) => money ? inrShort(v) : v} />
          <Area type="monotone" dataKey={dataKey} stroke={TEAL} strokeWidth={2.5} fill="url(#ga)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Bars({ data, keys, colors = CHART_COLORS, height = 260, money = false, stacked = false }) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
        <BarChart data={data} margin={{ left: 4, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} tickFormatter={(v) => money ? inrShort(v).replace("₹", "") : v} width={40} />
          <Tooltip formatter={(v) => money ? inrShort(v) : v} />
          {keys.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {keys.map((k, i) => <Bar key={k} dataKey={k} stackId={stacked ? "s" : undefined} fill={colors[i % colors.length]} radius={stacked ? 0 : [4, 4, 0, 0]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Donut({ data, money = false, height = 260 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ height }} className="relative">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="82%" paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => money ? inrShort(v) : v} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 mb-8 grid place-items-center">
        <div className="text-center"><p className="text-[11px] text-slate-400">Total</p><p className="font-display text-lg font-bold text-[#0B132B]">{money ? inrShort(total) : total}</p></div>
      </div>
    </div>
  );
}

export function RankTable({ cols, rows, render }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>{cols.map((c) => <th key={c} className="whitespace-nowrap px-4 py-3">{c}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{rows.map(render)}</tbody>
      </table>
    </div>
  );
}

// Semicircular gauge / speedometer for a percentage KPI.
export function Gauge({ value, label, color = TEAL, height = 180 }) {
  const v = Math.round(Math.max(0, Math.min(100, value || 0)));
  const data = [{ name: label, value: v, fill: color }];
  return (
    <div style={{ height }} className="relative" data-testid={`gauge-${label}`}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
        <RadialBarChart innerRadius="72%" outerRadius="100%" data={data} startAngle={180} endAngle={0}>
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar background={{ fill: "#eef2f7" }} dataKey="value" cornerRadius={10} angleAxisId={0} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 mb-4 grid place-items-center">
        <div className="text-center"><p className="font-display text-3xl font-bold text-[#0B132B]">{v}%</p><p className="text-xs text-slate-400">{label}</p></div>
      </div>
    </div>
  );
}

// Payment-lifecycle funnel (CSS bars — no fabricated data).
export function Funnel({ data, money = true }) {
  const max = Math.max(...data.map((d) => d.count || 0), 1);
  const palette = ["#0EA5E9", "#0891B2", "#0D9488", "#F59E0B", "#14B8A6", "#10B981"];
  return (
    <div className="space-y-2.5" data-testid="collection-funnel">
      {data.map((s, i) => {
        const w = Math.max(6, Math.round(100 * (s.count || 0) / max));
        return (
          <div key={s.stage}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-[#0B132B]">{s.stage}</span>
              <span className="text-slate-500">{s.count}{money ? ` • ${inrShort(s.amount)}` : ""}</span>
            </div>
            <div className="mt-1 h-7 overflow-hidden rounded-md bg-slate-100">
              <div className="flex h-7 items-center justify-end rounded-md pr-2 text-[10px] font-bold text-white transition-all"
                style={{ width: w + "%", background: palette[i % palette.length] }}>{w}%</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
